import { useEffect, useRef, useState } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

export interface TickCounterProps {
  /** The numeric value to display. Any change re-triggers the tick-up animation. */
  value: number | string;
  className?: ClassName;
}

/**
 * Displays a number that animates up (slight translate + fade) when its
 * value changes. Used wherever a count would otherwise blink between
 * states — "X places", "Day N of M", etc.
 *
 * No-op on the first render (so the initial value renders calmly), and
 * skipped entirely under `prefers-reduced-motion` via motion-safe:.
 */
export const TickCounter = ({ value, className }: TickCounterProps) => {
  // `tick` increments every time `value` changes after mount. We use it as
  // the `key` of the inner span so the kawaii-tick CSS animation replays
  // on every value change (CSS animations don't replay on class re-apply).
  const [tick, setTick] = useState(0);
  const firstRenderRef = useRef(true);

  useEffect(() => {
    // `value` isn't read inside the body — it's the sentinel whose change
    // refires this effect. `void value` keeps the linter aware it
    // belongs in the dep array.
    void value;
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    setTick((t) => t + 1);
  }, [value]);

  return (
    <span className={twx('inline-block', className)}>
      <span
        key={tick}
        className={twx('inline-block', tick > 0 && 'motion-safe:animate-kawaii-tick')}
      >
        {value}
      </span>
    </span>
  );
};
